/**
 * The STILLSUN icon set — one drawing per idea, used by every surface.
 *
 * Requiem is a hard-edged skin: sashes, chevrons, no radius, no glow. The icons follow.
 * Every glyph is stroke-only on a 24-unit grid at 1.8 weight, round caps and joins so a
 * 16 px row icon does not fur, `currentColor` so it inherits moon / lilac / accent from
 * whatever line it sits on. Geometry prefers diamonds, hexes and chevrons over circles;
 * the only fills are zero-length paths, which the round cap turns into a tiny dot.
 *
 * Two exports over one table of paths, so the vanilla HUD and the Preact hub can never
 * drift apart:
 *   - `icon(name, size?)` → an SVG string for `innerHTML` (hud.ts, abilitybar.ts).
 *   - `<Icon name />`     → the same drawing as a Preact node (hub/*, overlay.tsx).
 *
 * Everything here is drawn by hand for this project; nothing is downloaded, so there is
 * no licence to log in ASSETS.md.
 */

import {
  CallingId,
  ITEMS,
  MAX_DURABILITY,
  WEAPONS,
  WEAPON_FALL,
  type ItemDef,
} from '@stillsun/shared';
import type { VNode } from 'preact';
import atlasManifest from '../../public/assets/gen/icons/atlas.json';

/* eslint-disable @typescript-eslint/naming-convention -- these are drawings, not code */
const PATHS = {
  // ------------------------------------------------------------- the Callings
  /** Vanguard — a breach plate: a hull shield split by the door it just went through. */
  vanguard: '<path d="M12 2.8 20.5 6v6.4L12 21.2 3.5 12.4V6z"/><path d="M7.5 11h9"/>',
  /** Warden — the drone that brings you home: a cross carried inside a diamond. */
  warden:
    '<path d="M12 2.8 21.2 12 12 21.2 2.8 12z"/><path d="M12 7.8v8.4M7.8 12h8.4"/><path d="M12 12h.01"/>',
  /** Shade — a veil drawn across the eye. */
  shade:
    '<path d="M2.6 12 12 6.4 21.4 12 12 17.6z"/><path d="M9.4 12 12 9.4 14.6 12 12 14.6z"/><path d="M4.6 19.4 19.4 4.6"/>',
  /** Tinker — the eight-tooth gear, cut square. */
  tinker:
    '<path d="M12 8.4 15.6 12 12 15.6 8.4 12z"/><path d="M12 2.8v3.2M12 18v3.2M2.8 12H6M18 12h3.2"/><path d="M5.5 5.5 7.7 7.7M16.3 16.3l2.2 2.2M18.5 5.5l-2.2 2.2M7.7 16.3l-2.2 2.2"/>',

  // ------------------------------------------------ the twelve abilities (Q/F/X)
  bulwark: '<path d="M4 16h16"/><path d="M6.5 16V9.5L12 6.5l5.5 3V16"/>',
  breach: '<path d="M3 12h10"/><path d="M9.5 7.5 14 12l-4.5 4.5"/><path d="M18 5.5v13"/>',
  surge: '<path d="M13.5 3 6.5 13H11l-1 8 7.5-10.5H13z"/>',
  mend: '<circle cx="12" cy="13" r="4.2"/><path d="M12 10.8v4.4M9.8 13h4.4"/><path d="M5 8.5 8.6 11M19 8.5 15.4 11"/>',
  beacon:
    '<path d="M12 21V9.5"/><circle cx="12" cy="8" r="2.6"/><path d="M6.8 6.2a7.2 7.2 0 0 1 10.4 0"/>',
  suppress: '<path d="M3 18.5h18"/><path d="M5 18.5a7 7 0 0 1 14 0"/><path d="M12 11.5v-4"/>',
  veil: '<path d="M2.5 12S6.4 6.5 12 6.5 21.5 12 21.5 12 17.6 17.5 12 17.5 2.5 12 2.5 12z"/><path d="M4.5 19.5 19.5 4.5"/>',
  pulse:
    '<circle cx="12" cy="12" r="2"/><path d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6"/><path d="M15.8 8.2a5.4 5.4 0 0 1 0 7.6"/><path d="M5.6 5.6a9 9 0 0 0 0 12.8"/>',
  silence: '<path d="M4 9.5h3L11 6v12L7 14.5H4z"/><path d="M15 9.5 20 15M20 9.5 15 15"/>',
  sentry:
    '<path d="M12 13v8M12 21l-4.5 1M12 21l4.5 1"/><circle cx="10" cy="9.5" r="3"/><path d="M13 9h6"/>',
  hijack:
    '<circle cx="12" cy="12" r="3"/><path d="M3.5 12h5.5M15 12h5.5"/><path d="M18.5 9.5 21 12l-2.5 2.5"/>',
  fabricator:
    '<path d="M4 20.5V11h16v9.5z"/><path d="M8 11V7.5h8V11"/><path d="M12 3v3.5M9.5 4.5 12 6.5l2.5-2"/>',

  // -------------------------------------------------------- the order of service
  /** Step I — the mark a Calling brands you with. */
  calling: '<path d="M12 3.2 19.6 12 12 20.8 4.4 12z"/><path d="M12 8 15.2 12 12 16 8.8 12z"/>',
  /** Step II — the case your hands come out of. */
  loadout: '<path d="M3.6 8.8h16.8v11.6H3.6z"/><path d="M8 8.8V5h8v3.8"/><path d="M10 13.6h4"/>',
  /** Step III — a pin driven into ground someone surveyed. */
  ground:
    '<path d="M12 21.4 5.6 11.2 12 2.6l6.4 8.6z"/><path d="M9.6 11.2 12 8l2.4 3.2-2.4 3.2z"/>',
  /** Step IV — the door out, and the one irreversible step through it. */
  deploy: '<path d="M4 3.6h8v16.8H4z"/><path d="M9.6 12h10.8"/><path d="M16.6 8 20.4 12l-3.8 4"/>',

  // ------------------------------------------------------------------ Lanternfall
  workbench: '<path d="M3 20.6h18"/><path d="M6.6 20.6 14 13.2"/><path d="M13 5 19 11l2-2-6-6z"/>',
  vendor:
    '<path d="M12.6 3H21v8.4L11.4 21 3 12.6z"/><path d="M17 7.2h.01"/><path d="M8.4 11.2l4 4"/>',
  contracts:
    '<path d="M5.4 3h8.4L18.6 7.8V21H5.4z"/><path d="M13.4 3v5h5.2"/><path d="M8.6 12.6h6.6M8.6 16.4h4.6"/>',
  marks:
    '<path d="M12 2.8 14.5 8.6 20.8 9.2 16 13.4 17.4 19.6 12 16.3 6.6 19.6 8 13.4 3.2 9.2l6.3-.6z"/>',
  terminal:
    '<path d="M3 4.4h18v13.2H3z"/><path d="M7 9.2 9.6 11.8 7 14.4"/><path d="M12.6 14.4h4.6"/><path d="M8 21h8"/>',
  roadmap:
    '<path d="M12 2.8v18.4"/><path d="M12 5h7.4l2 2.6-2 2.6H12z"/><path d="M12 13.2H4.6l-2 2.6 2 2.6H12z"/>',
  changelog:
    '<path d="M12 2.8 21 7.4 12 12 3 7.4z"/><path d="M3 12 12 16.6 21 12"/><path d="M3 16.6 12 21.2 21 16.6"/>',
  choir:
    '<path d="M12 2.8v18.4"/><path d="M8 6.4v11.2M16 6.4v11.2"/><path d="M4 9.6v4.8M20 9.6v4.8"/>',
  settings:
    '<path d="M3 7h9M16 7h5M3 12h5M12 12h9M3 17h9M16 17h5"/><path d="M14 4.4v5.2M10 9.4v5.2M14 14.4v5.2"/>',
  stash: '<path d="M3.4 4.4h17.2v15.2H3.4z"/><path d="M3.4 12h17.2M12 4.4v15.2"/>',
  skills:
    '<path d="M12 3.2 14.2 5.6 12 8 9.8 5.6z"/><path d="M12 8v4.4M12 12.4H6.4v3M12 12.4h5.6v3"/><path d="M6.4 15.4 8.6 18l-2.2 2.6L4.2 18z"/><path d="M17.6 15.4 19.8 18l-2.2 2.6L15.4 18z"/>',

  // ------------------------------------------------------------------ deploy step
  /** Raid — a full band inside one hull. */
  raid: '<path d="M12 2.8 20.6 7.6v8.8L12 21.2 3.4 16.4V7.6z"/><path d="M9.2 10.4h.01M14.8 10.4h.01M12 15h.01"/>',
  /** Expedition — one to four, and a bearing. */
  expedition: '<path d="M12 2.6 20.8 21 12 16.6 3.2 21z"/><path d="M12 16.6V8.4"/>',
  /** The Spine's three decks. */
  deck: '<path d="M3.4 4.4h17.2v15.2H3.4z"/><path d="M3.4 9.4h17.2M3.4 14.6h17.2"/>',
  /** Squad — one going out with two beside them. */
  squad:
    '<path d="M12 3.6 14.2 6.4 12 9.2 9.8 6.4z"/><path d="M5.6 20.4v-3.2L12 14l6.4 3.2v3.2"/><path d="M4 10 5.6 12 4 14 2.4 12z"/><path d="M20 10 21.6 12 20 14l-1.6-2z"/>',

  // ---------------------------------------------------------- ledger and economy
  /** Wick — currency, because a burnable thing was the first thing anyone traded. */
  wick: '<path d="M12 2.6 16.2 9.4 12 15.2 7.8 9.4z"/><path d="M12 15.2v5.6"/><path d="M8.8 20.8h6.4"/>',
  level: '<path d="M3.6 13.4 12 5l8.4 8.4"/><path d="M3.6 19 12 10.6 20.4 19"/>',
  xp: '<path d="M3 20.8h18"/><path d="M5.6 20.8v-5M10.4 20.8v-9.4M15.2 20.8v-6.6M20 20.8V4.6"/>',
  points: '<path d="M12 3.2 20.8 12 12 20.8 3.2 12z"/><path d="M12 8v8M8 12h8"/>',
  insurance:
    '<path d="M12 2.8 19.6 6v6.4c0 4.2-3.2 6.9-7.6 8.6-4.4-1.7-7.6-4.4-7.6-8.6V6z"/><path d="m8.4 11.6 2.6 2.6 4.6-5.2"/>',
  satchel:
    '<path d="M4.4 8.8h15.2v11.6H4.4z"/><path d="M4.4 8.8 7 4.4h10L19.6 8.8"/><path d="M9.6 8.8v2.8h4.8V8.8"/>',

  // ------------------------------------------------------------ stash slot kinds
  /**
   * Weapon — a blade over its crossguard. Phase 2c retired the gun profile that used to live
   * here: there is no hitscan weapon left in the game, so the generic "weapon" mark is a stick.
   */
  weapon:
    '<path d="M3.4 20.6 6.2 17.8"/><path d="M5.2 16.8 17.4 4.6l2.8-1.2-1.2 2.8L7 18.4z"/><path d="M4.6 14.6 9.4 19.4"/>',
  consumable:
    '<path d="M9.4 3h5.2"/><path d="M10.6 3v6L7.6 20.6h8.8L13.4 9V3"/><path d="M8.8 14.4h6.4"/>',
  material:
    '<path d="M12 2.8 21 8v8l-9 5.2L3 16V8z"/><path d="m3 8 9 5.2L21 8"/><path d="M12 13.2v8"/>',
  valuable:
    '<path d="M7.6 3.4h8.8L21 9.4 12 20.6 3 9.4z"/><path d="M3 9.4h18"/><path d="M7.6 3.4 12 9.4l4.4-6M12 9.4v11.2"/>',
  stim: '<path d="M14.6 3.2 20.8 9.4"/><path d="M17.4 6 6.6 16.8l-3.4 4 .6-4z"/><path d="M12 8.6 15.4 12"/>',
  frag: '<path d="M9.4 4.2h5.2v2.4H9.4z"/><path d="M12 6.6v2"/><path d="M7.4 12.6 12 8.6l4.6 4v4.8L12 21.2l-4.6-3.8z"/>',

  // ------------------------------------------------------------ the paperdoll
  //
  // Five pieces of a body, drawn as kit rather than as anatomy: a hood, a plate, greaves,
  // boots, gloves. Each one reads at 20px inside a doll slot and at 14px in a picker chip,
  // which is why none of them carries an internal detail smaller than two units.
  /** Head — a hood with the lantern slit cut across the eyes. */
  'armor-head':
    '<path d="M12 3.2c3.6 0 5.8 2.4 5.8 6v4.4L12 20.8 6.2 13.6V9.2c0-3.6 2.2-6 5.8-6z"/><path d="M8.6 10.4h6.8"/>',
  /** Chest — hull plate over a sternum seam, cut square at the shoulders. */
  'armor-chest':
    '<path d="M4.6 5.6 12 3.8l7.4 1.8v6.2c0 4-3 6.6-7.4 8.4-4.4-1.8-7.4-4.4-7.4-8.4z"/><path d="M12 3.8v15.4"/><path d="M4.8 9.4h14.4"/>',
  /** Legs — greaves: two plates, knee-banded. */
  'armor-legs':
    '<path d="M7.2 3.6h9.6l-1 7.4-1.4 9.4h-4.8L8.2 11z"/><path d="M8.2 11h7.6"/><path d="M12 3.6v16.8"/>',
  /** Feet — a boot: sole, ankle, and the cuff above it. */
  'armor-feet':
    '<path d="M7.4 3.8h4.4v9.4l6.6 3.4v3.6H5.2v-3.6z"/><path d="M5.2 16.6h13.2"/><path d="M7.6 8.4h4.2"/>',
  /** Hands — a gauntlet: cuff, back plate, and the four knuckle bands. */
  'armor-hands':
    '<path d="M5.4 9.2h11.2l3 3.4-3 3.4H5.4z"/><path d="M8.4 5.6h8.2l1.4 3.6"/><path d="M9 12.6h.01M12 12.6h.01M15 12.6h.01"/>',
  /**
   * Shoulders — a mantle: the yoke across the top and a capped plate hanging off each end.
   * Phase 2c's sixth slot; the only armour glyph with no gun-era ancestor.
   */
  'armor-shoulders':
    '<path d="M4.2 12.4V9.6L8 7l4-1.6L16 7l3.8 2.6v2.8"/><path d="M4.2 12.4 6.4 15.8h3.2l1-3.4"/><path d="M19.8 12.4l-2.2 3.4h-3.2l-1-3.4"/>',

  // ------------------------------------------------- items with no rendered GLB
  //
  // The stats-only armour slots above, plus everything below, have no mesh in the crowd bake and
  // therefore no cell in `assets/gen/icons/atlas.json`. `ItemDef.icon` names one of these instead
  // — see the module header of `packages/shared/src/items.ts`. Same hand as the rest: 24-unit
  // grid, 1.8 stroke, diamonds and chevrons, nothing finer than two units.

  /** Tallow Stim — an ampoule with its level line, tapered to the needle. */
  'consumable-stim':
    '<path d="M9.6 3.2h4.8"/><path d="M12 3.2v3.4"/><path d="M8.6 6.6h6.8l-1 10.6-2.4 3.6-2.4-3.6z"/><path d="M9.2 12.4h5.6"/>',
  /** Warden Kit — a field case, cross on the lid. */
  'consumable-kit':
    '<path d="M3.4 8.4h17.2v11.2H3.4z"/><path d="M8.6 8.4V5.6h6.8v2.8"/><path d="M12 11.2v5.6M9.2 14h5.6"/>',
  /** Bell-Bronze Scrap — two shards off the same casting. */
  'material-scrap':
    '<path d="M3.4 9.6 8.6 4.4l5.2 3.2-1.6 5.6z"/><path d="m13.2 12.4 6.4 1.2-2.2 6.2-7.4-1.4z"/>',
  /** Chime Wire — wound between two posts, still humming. */
  'material-wire':
    '<path d="M3.4 6.4v11.2M20.6 6.4v11.2"/><path d="M5.6 8 18.4 11 5.6 14l12.8 3"/>',
  /** Ember Glass — a fused lens with the light still crossing it. */
  'material-glass':
    '<path d="M8.4 3.4h7.2l4 8.6-4 8.6H8.4l-4-8.6z"/><path d="M9.6 8.2 14.4 12l-4.8 3.8"/>',
  /** Hymn Shard — a splinter, and the three ticks of the note it will not stop holding. */
  'material-shard':
    '<path d="M13.6 2.8 17.4 12l-4.6 9.2L8 13.6l2-4.8z"/><path d="m3.2 8.4 3.2 1.4M3.2 15.6l3.4-1.2M20.8 8.4l-3 1.6"/>',
  /** Choir Core — a mind inside a mind, held in four brackets. */
  'material-core':
    '<path d="M12 6.6 17.4 12 12 17.4 6.6 12z"/><path d="M12 9.6 14.4 12 12 14.4 9.6 12z"/><path d="M4.6 4.6h3.2M4.6 4.6v3.2M19.4 4.6h-3.2M19.4 4.6v3.2M4.6 19.4h3.2M4.6 19.4v-3.2M19.4 19.4h-3.2M19.4 19.4v-3.2"/>',
  /** Congregant's Tag — a punched tag with a name and a pew number on it. */
  'valuable-tag':
    '<path d="M6.4 4.4h11.2v15.2H6.4z"/><path d="M12 7.6 13.2 8.8 12 10 10.8 8.8z"/><path d="M8.8 13.2h6.4M8.8 16.2h4.2"/>',
  /** Hymnal Slate — ruled for a stave, and still ruled. */
  'valuable-slate':
    '<path d="M4.4 3.4h15.2v17.2H4.4z"/><path d="M7.4 7.6h9.2M7.4 10.6h9.2M7.4 13.6h9.2M7.4 16.6h5.6"/>',
  /** Cathedral Relic — a reliquary: a little roof over something that outlasted the tower. */
  'valuable-relic':
    '<path d="M5.4 9.6 12 3.4l6.6 6.2v10.8H5.4z"/><path d="M12 11v6.2M9.4 13.6h5.2"/>',

  // ------------------------------------------------- weapon families (kill feed / bar)
  //
  // Two families, not four: a Vanguard swings something with an edge on it and a Warden swings
  // something with a light on it. The blade is `weapon` above; this is the other half.
  /** The Warden's line: a haft with a faceted head — staff, censer, wand. */
  wpnStaff: '<path d="M7.4 20.8 14.6 9"/><path d="M15.4 3 19.8 6.2l-2.4 4.4L12.8 7.4z"/>',
  /** No weapon: a fall, the Band, the deck itself. Two strokes crossed. */
  wpnNone: '<path d="M4.6 5.4 19.4 18.6"/><path d="M19.4 5.4 4.6 18.6"/>',
  /** A fall: a figure dropping, arms up, two motion ticks above. Used by the kill feed for WEAPON_FALL. */
  fall: '<circle cx="12" cy="7.2" r="2"/><path d="M12 9.4v5.2M12 11l-3.4-2.6M12 11l3.4-2.6M12 14.6l-2.8 4.6M12 14.6l2.8 4.6"/><path d="M6 3.2v3M18 3.2v3"/>',
  /** Map — folded, and off until the terrain pass lands. */
  map: '<path d="M3 6.4 9 4l6 2.4 6-2.4v13.6l-6 2.4-6-2.4-6 2.4z"/><path d="M9 4v13.6M15 6.4V20"/>',

  // --------------------------------------------------------------------- the raid
  extract:
    '<path d="M14 3.4h6.6v17.2H14"/><path d="M3.4 12h11"/><path d="M10.8 8.4 14.4 12l-3.6 3.6"/>',
  pip: '<path d="M12 5.6 18.4 12 12 18.4 5.6 12z"/>',
  downed:
    '<path d="M12 3.2 20.8 12 12 20.8 3.2 12z"/><path d="M8.4 10.4 12 14l3.6-3.6"/><path d="M12 6.6v1.6"/>',
  revive:
    '<path d="M12 3.2 20.8 12 12 20.8 3.2 12z"/><path d="M8.4 13.6 12 10l3.6 3.6"/><path d="M12 17.4v-1.6"/>',
  stand:
    '<path d="M12 3.2 13.8 5.2 12 7.2 10.2 5.2z"/><path d="M12 7.4v6.4"/><path d="M12 9 7.4 11.4M12 9l4.6 2.4"/><path d="M12 13.8 8.6 20.6M12 13.8l3.4 6.8"/>',
  crouch:
    '<path d="M12 4.8 13.8 6.8 12 8.8 10.2 6.8z"/><path d="M12 9v4"/><path d="M12 10.2 7.8 12.2M12 10.2l4.2 2"/><path d="M12 13 8.2 16.4v4.2M12 13l3.6 3.4"/>',
  prone:
    '<path d="M4.4 10.6 6.2 12.4 4.4 14.2 2.6 12.4z"/><path d="M6.4 12.4h11.2"/><path d="M9.4 12.4 8 16.4M13.2 12.4l1.4 4"/><path d="M17.6 12.4 21.4 10.8"/>',
  sprint:
    '<path d="M13.6 3.6 15.4 5.6 13.6 7.6 11.8 5.6z"/><path d="M14.6 8.4 10.4 12.2l2.6 2.8.8 5.6"/><path d="M10.4 12.2 6.6 13.4"/><path d="M13 15 8.4 20.6"/><path d="M2.6 8.4h4.2M2.6 12.2h2.8"/>',
  skip: '<path d="M2.6 20.4h18.8"/><path d="M3.6 18.4 8 8.6l4.4 9.8"/><path d="M12.4 18.4 16.4 10l4 6.4"/>',
  ammo: '<path d="M8.4 3.4h7.2v4H8.4z"/><path d="M9.4 7.4h5.2v13.2H9.4z"/><path d="M9.4 11.4h5.2M9.4 15.2h5.2"/>',
  reload:
    '<path d="M20.4 12a8.4 8.4 0 1 1-2.7-6.2"/><path d="M20.8 3.4v4.6h-4.6"/><path d="M12 12h.01"/>',
  chat: '<path d="M3.4 4.4h17.2v11.4H9.8l-6.4 4.8z"/><path d="M7.6 8.4h8.8M7.6 11.8h5.4"/>',
  /** Kill feed: killer → victim. A chevron, not a font's idea of an arrow. */
  killarrow: '<path d="M3.4 12h14.2"/><path d="m13.4 7.6 4.4 4.4-4.4 4.4"/>',
  /** Kill feed: the shot that went through the lantern slit. */
  headshot:
    '<path d="M12 7.6 16.4 12 12 16.4 7.6 12z"/><path d="M12 2.8v3.2M12 18v3.2M2.8 12H6M18 12h3.2"/>',
  feedback:
    '<path d="M3.6 9.4h4l8-4.6v14.4l-8-4.6h-4z"/><path d="M7.6 14.6v5.8h3.2l-1.2-5.8"/><path d="M19 9.6v4.8"/>',
  inbox: '<path d="M3.4 13.4h5l1.6 3.2h4l1.6-3.2h5"/><path d="M6.2 4.4h11.6l2.8 9v7.2H3.4V13.4z"/>',
  account:
    '<path d="M12 3.6 14.4 6.6 12 9.6 9.6 6.6z"/><path d="M4.6 20.4v-2.8L12 14l7.4 3.6v2.8"/>',
  secure:
    '<path d="M5.4 10.4h13.2v10.2H5.4z"/><path d="M8.4 10.4V7.4L12 4.4l3.6 3v3"/><path d="M12 14v3"/>',
  soundOn:
    '<path d="M3.6 9.2h3.6L11.8 5.2v13.6L7.2 14.8H3.6z"/><path d="M15.2 9.4a4 4 0 0 1 0 5.2M18.4 6.8a8 8 0 0 1 0 10.4"/>',
  soundOff:
    '<path d="M3.6 9.2h3.6L11.8 5.2v13.6L7.2 14.8H3.6z"/><path d="m15.6 9.6 5.2 4.8M20.8 9.6l-5.2 4.8"/>',

  // ------------------------------------------------------------------- tip cards
  /** TIP — a lantern, which is what the Compact hands a new Waker. */
  tip: '<path d="M12 3 16.4 8v6.2H7.6V8z"/><path d="M12 14.2v3"/><path d="M9.4 17.2h5.2M10.6 20.4h2.8"/>',
  /** TRICK — the spark the veterans keep to themselves. */
  trick: '<path d="M12 2.8 13.8 10.2 21.2 12l-7.4 1.8L12 21.2l-1.8-7.4L2.8 12l7.4-1.8z"/>',
  /** THE BAND — a world with one strip of twilight around it. */
  lore: '<path d="M12 3 20.6 12 12 21 3.4 12z"/><path d="M4.6 9.6h14.8M4.6 14.4h14.8"/>',
  // ---------------------------------------------------------------- the Marks
  //
  // One drawing per Mark in `progression.MARKS`, keyed by id in `MARK_ICON` below. Same hand as
  // everything above: 24-unit grid, stroke only, diamonds and chevrons rather than circles, and
  // nothing that needs a fill to read. Where a Mark is about a *place* the glyph shows the place;
  // where it is about a *count* the glyph shows the thing being counted, so a wall of eighteen of
  // them scans as eighteen different deeds rather than eighteen variations on a star.

  /** First Light — the hatch, and the first daylight through it. */
  markFirstLight:
    '<path d="M4.2 20.6h15.6"/><path d="M7.4 20.6V9.8L12 5.2l4.6 4.6v10.8"/><path d="M12 20.6v-6.2"/><path d="M12 2.6v2"/>',
  /** Choirbreaker — a stave, snapped. */
  markChoirbreaker:
    '<path d="M3.2 7.4h7.2M3.2 12h6M3.2 16.6h7.2"/><path d="M13.6 7.4h7.2M14.6 12h6.2M13.6 16.6h7.2"/><path d="M13 3.6 10.4 20.4"/>',
  /** Lamplighter — a lantern with the shot already through it. */
  markLamplighter:
    '<path d="M12 6.4 15.8 10v6.4H8.2V10z"/><path d="M9.4 3.6h5.2"/><path d="M12 16.4v4"/><path d="M4.6 4.6 8.2 8.2M19.4 4.6 15.8 8.2"/>',
  /** Dusk Walker — a figure still walking while the Still goes down. */
  markDusk:
    '<path d="M2.6 20.6h18.8"/><path d="M6.2 20.6 9 14l3.4 3 1.4 3.6"/><path d="M9.6 10.6 11.4 12.4 9.6 14.2 7.8 12.4z"/><path d="M16.6 8.4 19.4 11.2 16.6 14"/>',
  /** Not a Sound — a bell with a bar through it. */
  markQuiet:
    '<path d="M6.6 16.4h10.8"/><path d="M8.4 16.4V11a3.6 3.6 0 0 1 7.2 0v5.4"/><path d="M12 5.4V3.6"/><path d="M4.6 19.4 19.4 4.6"/>',
  /** Skipping Stone — the arc, and where it touched down. */
  markSkip:
    '<path d="M2.6 18.6h18.8"/><path d="M3.4 18.6c3-8.4 6.4-8.4 9 0"/><path d="M13 18.6c2.2-5.6 4.6-5.6 6.6 0"/><path d="M7.6 10.4 9 11.8 7.6 13.2 6.2 11.8z"/>',
  /** Ledge Runner — a step, taken up. */
  markMantle:
    '<path d="M2.6 20.4h6.8v-5.2h5.6V10h6.4"/><path d="M14.6 6.4 16.4 8.2 14.6 10l-1.8-1.8z"/><path d="M17.6 3.6h3.6v3.6"/>',
  /** Bad Landing — straight down, and the deck that stopped it. */
  markFall:
    '<path d="M12 3.4v11.2"/><path d="M8 10.6 12 14.6l4-4"/><path d="M3.4 18.6h17.2"/><path d="M6.4 21.2 8 18.6M17.6 21.2 16 18.6"/>',
  /** Warden of the Band — one hand under another. */
  markWarden:
    '<path d="M12 3.4 14.2 6.2 12 9 9.8 6.2z"/><path d="M4.6 20.6v-3l7.4-3.4 7.4 3.4v3"/><path d="M8.4 12.2 12 9.6l3.6 2.6"/>',
  /** Wick Baron — a stack of them, lit. */
  markBaron:
    '<path d="M12 2.6 15.2 7 12 11.4 8.8 7z"/><path d="M12 11.4 15.2 15.8 12 20.2 8.8 15.8z"/><path d="M3.4 20.8h17.2"/>',
  /** Heart Taker — a core, carried out in a cage. */
  markCore:
    '<path d="M6.4 6.4h11.2v11.2H6.4z"/><path d="M12 8.8 15.2 12 12 15.2 8.8 12z"/><path d="M12 3.2v3.2M12 17.6v3.2M3.2 12h3.2M17.6 12h3.2"/>',
  /** Song Cutter — the verse, cut mid-line. */
  markCantor:
    '<path d="M4.2 8h15.6M4.2 12h6.4M13.4 12h6.4M4.2 16h15.6"/><path d="M14.6 4.6 9.4 19.4"/>',
  /** Organ Stop — pipes, and the bar laid across them. */
  markSexton: '<path d="M6 20.4V7.2M10 20.4V4.2M14 20.4V6M18 20.4V9"/><path d="M3.4 15.2h17.2"/>',
  /** Vergerslayer — the conductor's diamond, struck through. */
  markVerger:
    '<path d="M12 2.8 19.2 12 12 21.2 4.8 12z"/><path d="M12 7.6 15.6 12 12 16.4 8.4 12z"/><path d="M4.2 19.8 19.8 4.2"/>',
  /** Reliable — a page the Compact has already stamped. */
  markContracts:
    '<path d="M5.4 3.2h8L18.6 8v12.8H5.4z"/><path d="M13.4 3.2V8h5.2"/><path d="m8.4 14.2 2.4 2.4 4.6-5"/>',
  /** Tinker's Hands — the hammer, over the square it is working. */
  markCrafter:
    '<path d="M7.6 13.4h8.8v7.2H7.6z"/><path d="M14.2 3.4 20.6 9.8"/><path d="M17.4 6.6l-6 6"/><path d="M12.6 4.6h4v4"/>',
  /** Old Hand — a tally, and the stroke that closes it. */
  markRaids:
    '<path d="M6 5.4v13.2M10 5.4v13.2M14 5.4v13.2M18 5.4v13.2"/><path d="M3.6 18.2 20.4 5.8"/>',
  /** Kindling — a candle already gone over. */
  markDeaths:
    '<path d="M3.4 20.6h17.2"/><path d="M8.2 20.6V11h4.4v9.6"/><path d="M12.6 11 17 6.6"/><path d="M17.4 2.8 19.2 5l-2.2 1.6-1.6-2z"/>',

  // Category fallbacks. A Mark this build has not been taught yet still gets a drawing that
  // says what *kind* of deed it is, keyed off its counter in `MARK_ICON`.
  markCombat: '<path d="M12 2.8 16.4 12 12 21.2 7.6 12z"/><path d="M8.6 12h6.8"/>',
  markTravel: '<path d="M3.4 18.6h17.2"/><path d="M5.4 18.6 12 6.4l6.6 12.2"/>',
  markLedger:
    '<path d="M4.4 4.4h15.2v15.2H4.4z"/><path d="M4.4 9.4h15.2"/><path d="M8.4 13.4h7.2M8.4 16.4h4.6"/>',

  /** Armour — the plate, cut square, with a shoulder line. */
  armor:
    '<path d="M12 2.8 20.2 5.6v7.2c0 4-3.4 6.6-8.2 8.4-4.8-1.8-8.2-4.4-8.2-8.4V5.6z"/><path d="M6.4 9.4h11.2"/>',

  // ------------------------------------------------------------ the workbench
  /** A finished part, on the sash of a ready job. */
  benchReady: '<path d="M4.4 12.6 9.6 17.8 19.6 6.6"/>',
  /** The bench lamp — the only lit thing in the room. */
  benchLamp:
    '<path d="M12 6.2 15.6 9.8v5.4H8.4V9.8z"/><path d="M12 15.2v4.4"/><path d="M8.6 19.6h6.8"/><path d="M12 2.6v2.6"/>',

  /** THE STILL SAYS — a sun four degrees up, and never moving. */
  still:
    '<path d="M2.6 18.6h18.8"/><path d="M7.6 18.6 12 12.6l4.4 6"/><path d="M12 8.4V4.6M5.6 10.6 3.4 8.4M18.4 10.6l2.2-2.2"/>',
} as const;
/* eslint-enable @typescript-eslint/naming-convention */

export type IconName = keyof typeof PATHS;

/** Attributes every glyph carries, so an icon renders right without waiting for a class. */
const ATTRS =
  'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"';

/** True for a name the set actually draws — lets callers narrow a `string` safely. */
export function isIconName(name: string): name is IconName {
  return Object.prototype.hasOwnProperty.call(PATHS, name);
}

/**
 * An SVG string for the vanilla surfaces (`hud.ts`, `abilitybar.ts`, `index.html`).
 * `size` is the box in CSS pixels; the stroke stays 1.8/24 of it, which is what keeps a
 * 16 px row icon and a 52 px Calling header looking like the same hand drew them.
 */
export function icon(name: IconName, size = 16): string {
  return `<svg class="ic" viewBox="0 0 24 24" width="${String(size)}" height="${String(size)}" ${ATTRS} aria-hidden="true">${PATHS[name]}</svg>`;
}

/**
 * The ability glyph by id. `AbilityDef.id` is a plain `string` in the shared contract, so
 * a Calling gaining an ability the client has not drawn yet gets a neutral diamond rather
 * than an empty cell.
 */
export function abilityIcon(id: string, size = 16): string {
  return isIconName(id) ? icon(id, size) : icon('pip', size);
}

export interface IconProps {
  name: IconName;
  /** Box in CSS pixels. 16 in rows, 20 in tabs and steps, 44–56 on a Calling card. */
  size?: number;
  class?: string;
}

/** The same drawing as a Preact node. Decorative by construction: never a label on its own. */
export function Icon(props: IconProps): VNode {
  const size = props.size ?? 16;
  return (
    <svg
      class={props.class === undefined ? 'ic' : `ic ${props.class}`}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: PATHS[props.name] }}
    />
  );
}

// ---------------------------------------------------------------- lookup tables

/** Calling id (`CallingId` in the shared contract) → its icon. */
export const CALLING_ICON: Record<number, IconName> = {
  1: 'vanguard',
  2: 'warden',
  3: 'shade',
  4: 'tinker',
};

/**
 * `ArmorSlot` (`ARMOR_SLOTS` in the shared contract) → the paperdoll glyph for that slot. All six
 * slots since Phase 2c: `shoulders` is the empty-slot silhouette the doll draws for the third
 * visible piece, and a missing key here would have left one square of the body blank.
 */
export const ARMOR_SLOT_ICON: Record<string, IconName> = {
  head: 'armor-head',
  shoulders: 'armor-shoulders',
  chest: 'armor-chest',
  hands: 'armor-hands',
  legs: 'armor-legs',
  feet: 'armor-feet',
};

/** `ItemDef.kind` → its icon. Unknown kinds fall back to the neutral diamond. */
export function itemKindIcon(kind: string | undefined): IconName {
  switch (kind) {
    case 'weapon':
      return 'weapon';
    case 'armor':
      return 'armor-chest';
    case 'consumable':
      return 'consumable';
    case 'material':
      return 'material';
    case 'valuable':
      return 'valuable';
    case 'key':
      return 'secure';
    default:
      return 'pip';
  }
}

/**
 * Mark id (`progression.MARKS`) → its drawing. Keyed by id rather than by index so a Mark added,
 * removed or reordered in the shared contract cannot silently re-label eighteen icons; anything
 * this build has not been taught falls through `markIcon` to a per-counter category glyph.
 */
export const MARK_ICON: Record<string, IconName> = {
  m_first_light: 'markFirstLight',
  m_choirbreaker: 'markChoirbreaker',
  m_headhunter: 'markLamplighter',
  m_dimming: 'markDusk',
  m_quiet: 'markQuiet',
  m_skip: 'markSkip',
  m_mantle: 'markMantle',
  m_fall: 'markFall',
  m_warden: 'markWarden',
  m_rich: 'markBaron',
  m_cores: 'markCore',
  m_cantor: 'markCantor',
  m_sexton: 'markSexton',
  m_verger: 'markVerger',
  m_contracts: 'markContracts',
  m_crafter: 'markCrafter',
  m_raids: 'markRaids',
  m_deaths: 'markDeaths',
};

/** The category a counter belongs to, for the fallback drawing. */
function markCategoryIcon(counter: string): IconName {
  if (
    counter.startsWith('kills') ||
    counter === 'kills' ||
    counter === 'headshots' ||
    counter === 'hymn_kills'
  ) {
    return 'markCombat';
  }
  if (counter.startsWith('extracts') || counter === 'raids' || counter === 'deaths')
    return 'markTravel';
  if (counter === 'skip_jumps' || counter === 'mantles' || counter === 'falls_survived')
    return 'markTravel';
  return 'markLedger';
}

/**
 * The drawing for a Mark. `counter` is the Mark's tracked counter key, used only when the id is
 * one this build has never seen — a new Mark shipped by the server still gets an honest glyph.
 */
export function markIcon(id: string, counter = ''): IconName {
  return MARK_ICON[id] ?? markCategoryIcon(counter);
}

/** Stance index (`PlayerState.stance`) → its icon, in the HUD's state line order. */
export const STANCE_ICONS: readonly IconName[] = ['stand', 'crouch', 'prone'];

/**
 * Weapon id (`WeaponDef.id` in the shared contract) → the family glyph the kill feed draws.
 * 0 — and anything the client has not been taught yet — is the crossed pair: the Band, a fall,
 * the deck itself.
 *
 * Phase 2c took `WeaponDef.slot` away with the guns, so the split is by Calling instead, which is
 * the split a player can actually see: a Vanguard swings an edge, a Warden swings a light.
 */
export function weaponIcon(weaponId: number): IconName {
  if (weaponId === WEAPON_FALL) return 'fall';
  const def = WEAPONS[weaponId];
  if (!def) return 'wpnNone';
  return def.calling === CallingId.Warden ? 'wpnStaff' : 'weapon';
}

// ============================================================== the item atlas
//
// Every weapon and every visible-armour look has a rendered GLB, and `tools/assetgen` bakes one
// 128 px thumbnail per mesh into `public/assets/gen/icons/atlas.{webp,png}` with a manifest beside
// it. `ItemDef.icon` therefore names one of two things: a cell in that atlas (a picture of the
// exact mesh the world will put on the body) or, for the stats-only slots and everything with no
// mesh at all, one of the drawn glyphs above. `iconAtlasCell` is the single place that decides
// which, so no caller has to know the difference.
//
// The manifest is *imported*, not fetched: Vite inlines the JSON at build time, which keeps
// `iconAtlasCell` synchronous (a sprite that arrives one frame after a glyph would flash) and
// makes a renamed cell a build error rather than a silent blank square.

/** The atlas image. WebP; `atlas.png` sits beside it for anything that cannot decode WebP. */
export const ICON_ATLAS_URL = '/assets/gen/icons/atlas.webp';

const ATLAS_COLUMNS = atlasManifest.columns;
const ATLAS_ROWS = atlasManifest.rows;

/** id → `[col, row]`, rows counting from the top as CSS `background-position` does. */
const ATLAS_CELLS: ReadonlyMap<string, readonly [number, number]> = (() => {
  const cells = new Map<string, readonly [number, number]>();
  for (const [id, pair] of Object.entries(atlasManifest.items as Record<string, number[]>)) {
    const col = pair[0];
    const row = pair[1];
    if (typeof col === 'number' && typeof row === 'number') cells.set(id, [col, row]);
  }
  return cells;
})();

/** The cell `id` names, or null when it is a drawn glyph (or nothing this build knows). */
export function iconAtlasCell(id: string): [number, number] | null {
  const cell = ATLAS_CELLS.get(id);
  return cell === undefined ? null : [cell[0], cell[1]];
}

/**
 * The drawn glyph for an item whose `icon` is not an atlas cell: the named glyph if the set has
 * it, else the mark for its kind, so an item shipped by a newer server still gets an honest
 * drawing rather than an empty square.
 */
export function itemGlyph(def: ItemDef | undefined): IconName {
  if (!def) return 'pip';
  if (isIconName(def.icon)) return def.icon;
  return itemKindIcon(def.kind);
}

export interface ItemIconProps {
  def: ItemDef | undefined;
  /** Box in CSS pixels. 28 in a bag tile, 34 on a paperdoll slot, 44 in a tooltip head. */
  size?: number;
  /**
   * 0..`MAX_DURABILITY`. Under full draws the wear bar under the icon; at 0 the piece is broken —
   * greyed, with the hairline cross through it. Omit for anything that does not wear.
   */
  durability?: number;
  class?: string;
}

/**
 * An item's picture: the atlas sprite when it has one, the drawn glyph when it does not, plus
 * the two things durability has to say at a glance — the bar, and the cross.
 */
export function ItemIcon(props: ItemIconProps): VNode {
  const size = props.size ?? 32;
  const cell = props.def === undefined ? null : iconAtlasCell(props.def.icon);
  const dur = props.durability;
  const broken = dur !== undefined && dur <= 0;
  const worn = dur !== undefined && dur < MAX_DURABILITY && dur > 0;
  const px = (n: number): string => `${String(Math.round(n))}px`;
  return (
    <span
      class={`itemicon${broken ? ' broken' : ''}${props.class === undefined ? '' : ` ${props.class}`}`}
      style={{ width: px(size), height: px(size) }}
    >
      {cell === null ? (
        <Icon name={itemGlyph(props.def)} size={Math.round(size * 0.8)} class="itemicon-glyph" />
      ) : (
        <span
          class="itemicon-sprite"
          style={{
            backgroundImage: `url(${ICON_ATLAS_URL})`,
            backgroundSize: `${px(size * ATLAS_COLUMNS)} ${px(size * ATLAS_ROWS)}`,
            backgroundPosition: `-${px(cell[0] * size)} -${px(cell[1] * size)}`,
          }}
        />
      )}
      {broken ? <span class="itemicon-crack" aria-hidden="true" /> : null}
      {worn ? (
        <span class="itemicon-wear" aria-hidden="true">
          <i style={{ width: `${String(Math.round((dur / MAX_DURABILITY) * 100))}%` }} />
        </span>
      ) : null}
    </span>
  );
}

/**
 * Dev-time proof that the two halves cover the whole item table: every `ITEMS[*].icon` must be
 * either an atlas cell or a glyph this file draws. A miss is a blank square in a bag, which is
 * exactly the kind of thing nobody notices until a player reports it — so it is shouted at the
 * console on every dev boot instead. Stripped from production by the `import.meta.env.DEV` guard.
 */
if (import.meta.env.DEV) {
  const missing: string[] = [];
  for (const def of Object.values(ITEMS)) {
    if (iconAtlasCell(def.icon) === null && !isIconName(def.icon)) {
      missing.push(`${def.id} → "${def.icon}"`);
    }
  }
  if (missing.length > 0) {
    console.error(
      `[stillsun] ${String(missing.length)} item icon(s) resolve to neither an atlas cell nor a drawn glyph:\n  ${missing.join('\n  ')}`,
    );
  }
}
